
import { Component, Vue } from "vue-property-decorator";

import { IRouteMeta } from "@/router/interface";
import program_routes from "@/api/routes/programs";

import Preloader from "@/components/Preloader.vue";
import Search from "@/components/Search.vue";
import CInfiniteScroll from "@/components/InfiniteScroll.vue";

import CProgramAssignmentsEdit, {
  IChangedProgramAssignment
} from "./components/Edit.vue";
import CProgramAssignmentsDelete, {
  IDeletedProgramAssignment
} from "./components/Delete.vue";

export interface IProgramAssignment {
  id: string;
  created_at: string;
  program_id: string;
  program_title: string;
  skill_id: string;
  skill_full_name: string;
  pillar_id?: string;
  pillar_full_name?: string;
  pillar_short_name?: string;
  pillar_color?: string;
  subscriptions_count: number;
}

export interface IProgramAssignmentData {
  current_page: number;
  next_page: boolean;
  assignments: IProgramAssignment[];
}

@Component({
  name: "VProgramAssignments",
  components: {
    Preloader,
    Search,
    CInfiniteScroll,
    CProgramAssignmentsEdit,
    CProgramAssignmentsDelete
  }
})
export default class VProgramAssignments extends Vue {
  public preload: boolean = false;
  private preload_infinite: boolean = false;

  private per_page: number = 100;
  private current_page: number = 0;
  private next_page: boolean = false;

  private search: string = this.$store.state.search;

  public assignments: IProgramAssignment[] = [];
  private current_user: IJWTUser | null = this.$store.state.currentUser;

  private watchers: Function[] = [];

  protected beforeDestroy() {
    this.watchers.forEach(unwatch => {
      unwatch();
    });
  }

  protected async created() {
    this.watchers.push(
      this.$store.watch(
        state => state.search,
        async search => {
          this.search = search;

          await this.clearAssignments();
        }
      )
    );

    this.watchers.push(
      this.$store.watch(
        state => state.currentUser,
        currentUser => {
          this.current_user = currentUser;
        }
      )
    );

    this.watchers.push(
      this.$store.watch(
        state => state.deletedProgramAssignment,
        (deletedProgramAssignment: IDeletedProgramAssignment) => {
          if (deletedProgramAssignment?.assignment_index != null) {
            this.$delete(
              this.assignments,
              deletedProgramAssignment.assignment_index
            );
          }
        }
      )
    );

    this.watchers.push(
      this.$store.watch(
        state => state.changedProgramAssignment,
        (changedProgramAssignment: IChangedProgramAssignment) => {
          if (changedProgramAssignment) {
            if (changedProgramAssignment.assignment_index != null) {
              this.$set(
                this.assignments,
                changedProgramAssignment.assignment_index,
                changedProgramAssignment.assignment
              );
            } else {
              this.assignments.unshift(changedProgramAssignment.assignment);
            }
          }
        }
      )
    );

    await this.clearAssignments();
  }

  private async clearAssignments() {
    this.preload = true;

    this.assignments = [];

    await this.loadAssignments();

    this.preload = false;
  }

  private async loadAssignments(page: number = 0) {
    this.preload_infinite = true;

    return this.$api
      .get(program_routes.assignments, {
        params: {
          page,
          per_page: this.per_page,
          search: this.search,
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: IProgramAssignmentData }) => {
        this.current_page = res.current_page;
        this.next_page = res.next_page;

        this.assignments.push(...res.assignments);
      })
      .finally(() => {
        this.preload_infinite = false;
      });
  }

  public async infiniteHandler() {
    if (this.next_page && !this.preload_infinite) {
      await this.loadAssignments(this.current_page + 1);
    }
  }

  public createAssignment() {
    this.$store.commit("changingProgramAssignment", {
      id: null,
      assignment_index: null
    });
  }

  public updateAssignment(id: string, assignment_index: number) {
    this.$store.commit("changingProgramAssignment", {
      id,
      assignment_index
    });
  }

  public deleteAssignment(id: string, assignment_index: number) {
    this.$store.commit("deletingProgramAssignment", {
      id,
      assignment_index
    });
  }

  public allowWrite() {
    return this.$api.allowWrite();
  }

  public allowDelete() {
    return this.current_user?.is_admin || this.current_user?.is_developer;
  }
}
